import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceAnchorType,
  IExperienceTemplate,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Switch,
  Typography
} from '@material-ui/core';
import { keyBy } from 'lodash';
import { AlertCircle, Check, Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSelector } from '../../../../../components/ColumnSelector';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { FormRow } from '../../../../../components/Form/FormRow';
import { RowsRendererScrollContainer } from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { TableSearch } from '../../../../../components/Table';
import { ToggleButtonGroupPills } from '../../../../../components/ToggleButtonGroupPills';
import { UserTable } from '../../../../../components/UserTable';
import {
  useUserTableColumnsAndSorters
} from '../../../../../components/UserTable/columns';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { PageToolbar, PageToolbarActions } from '../../../../../layout/PageToolbar';
import { Section } from '../../../../../layout/Section';
import { useStringQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { extractUniqueKValues } from '../../../../../services/rules';
import { now, Timestamp } from '../../../../../services/time';
import {
  getUserFailedFields,
  matchUser,
  matchUserBySearchTerm,
  toSearchTermRegexp,
  useUserRuleConfigSimple,
  useUserRuleConfigWithAutocompletion,
  useUsersByOrgId
} from '../../../../../services/user';
import { useTheme } from '../../../../../themes';
import { RuleBuilder, RuleRenderer } from '../../../components/RuleBuilder';
import { StandardFooter } from '../Footer';
import { StepBodyProps } from '../types';

const TABLE_HEIGHT = 500;

const InfoBanner = styled('div')`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(1)}px;
  padding: ${(p) => p.theme.spacing(1.5)}px ${(p) => p.theme.spacing(2)}px;
  background-color: #FEFAEC;
  border-radius: ${(p) => p.theme.spacing(1)}px;
  margin-bottom: 0;
`;

// TODO move to a more commonly accessible spot
export const MatchingUsers = ({
  users,
  loading,
  rule,
  referenceDate = now(),
  onClose,
}: {
  users: void | Doc<IUser>[];
  loading: boolean;
  rule: Rule<UserField> | null;
  referenceDate?: Timestamp;
  onClose?: () => void
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { columns, sorters } = useUserTableColumnsAndSorters(
    org.customUserFields,
    EMPTY_ARR
  );
  const [q, setQ] = useStringQueryParam('q', '');
  const [viewMode, setViewMode] = useState<'matches' | 'nonmatches'>('matches');

  const [selectedColumns, setSelectedColumns] = useState(() => {
    const defaultColumns = columns.filter(
      (c) =>
        c.key === 'name'
    );
    
    // If there's a rule, add columns for the fields mentioned in the rule
    if (rule) {
      const ruleFieldNames = extractUniqueKValues(rule);
      const columnsByKey = keyBy(columns, (c) => c.key);

      // Mapping for calculated fields
      const calculatedFieldMappings: any = {
        'calculatedFields.recurringDate.hireDate.current.count': 'anniversaryCount',
        'calculatedFields.recurringDate.hireDate.current.date': 'anniversaryDate',
        'calculatedFields.recurringDate.dateOfBirth.current.date': 'nextBirthday',
      };
      
      const ruleColumns = ruleFieldNames
        .map(fieldName => {
          const calculatedFieldKey = Object.keys(calculatedFieldMappings).find(key => 
            fieldName.includes(key)
          );
          if (calculatedFieldKey) {
            return columnsByKey[calculatedFieldMappings[calculatedFieldKey]];
          }

          return columnsByKey[fieldName] || 
                 columnsByKey[`customFields.${fieldName}`] ||
                 (fieldName.startsWith('customFields.') ? columnsByKey[fieldName] : null);
        })
        .filter(Boolean);

      const existingKeys = new Set(defaultColumns.map(col => col.key));

      const uniqueRuleColumns = ruleColumns.filter(ruleColumn => 
        !existingKeys.has(ruleColumn.key)
      );

      return [...defaultColumns, ...uniqueRuleColumns];
    }

    return defaultColumns;
  });

  const [matchingUsers, nonMatchingUsers, displayedUsers, failedFieldsByUser] = useMemo(() => {
    if (!users) {
      return [[], [], [], {}];
    }

    const ruleMatchedUsers = rule
      ? users.filter((u) => matchUser(u, rule, referenceDate))
      : users;

    const nonRuleMatchedUsers = rule
      ? users.filter((u) => !matchUser(u, rule, referenceDate))
      : [];

    const failedFields = rule ? nonRuleMatchedUsers?.reduce((acc, user: Doc<IUser>) => {
      acc[user.id] = getUserFailedFields(user, rule, referenceDate);
      return acc;
    }, {} as Record<string, Set<string>>) : {};

    console.log(failedFields);

    let usersToFilter = viewMode === 'matches' ? ruleMatchedUsers : nonRuleMatchedUsers;
    
    if (q) {
      const regexp = toSearchTermRegexp(q);
      usersToFilter = usersToFilter.filter(u => matchUserBySearchTerm(regexp, u));
    }
    
    return [ruleMatchedUsers, nonRuleMatchedUsers, usersToFilter, failedFields];
  }, [q, users, rule, referenceDate, viewMode]);

  const title = t('Preview participants who match the filters');
  if (loading || !users) {
    return (
      <>
        <Loader height={TABLE_HEIGHT} />
      </>
    );
  }

  const selected = (columns: any) => {
    console.log(columns);
    setSelectedColumns(columns);
  }

  return (
    <>
      <DialogActionsHeader
        left={(
          <Typography component='h2' variant='h4'>
            {title}
          </Typography>
        )}
        onClose={onClose}
      />
      <DialogContent>
        {(matchingUsers.length > 0 || nonMatchingUsers.length > 0) ? (
          <>
            <PageToolbar>
              <ToggleButtonGroupPills
                value={viewMode}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue) {
                    setViewMode(newValue);
                  }
                }}
                options={[
                  { value: 'matches', label: `Matches (${matchingUsers.length})` },
                  { value: 'nonmatches', label: `Non-matches (${nonMatchingUsers.length})` }
                ]}
              />
              <PageToolbarActions>
                <ColumnSelector
                  value={selectedColumns}
                  onChange={selected}
                  columns={columns}
                  requiredColumns={new Set(['name'])}
                  withAllOption
                  withSearch
                />
                <TableSearch
                  term={q}
                  onChange={setQ}
                  placeholder={t('users.overview.search')}
                />
              </PageToolbarActions>
            </PageToolbar>
            {viewMode === 'nonmatches' && (
              <InfoBanner>
                <AlertCircle size={20} />
                <Typography variant="body2">
                  Filters which don't match are highlighted in yellow
                </Typography>
              </InfoBanner>
            )}
            <RowsRendererScrollContainer height={TABLE_HEIGHT}>
              <UserTable
                ds={displayedUsers}
                columns={selectedColumns}
                sorters={sorters}
                dateFormat={org.dateFormats.MMDDYYYY}
                highlightedFields={viewMode === 'nonmatches' ? failedFieldsByUser : undefined}
              />
            </RowsRendererScrollContainer>
          </>
        ) : null}
      </DialogContent>
    </>
  );
};

const EnrollmentOptionsRow = styled('div')((p) => ({
  marginTop: p.theme.spacing()
}));

const EnrollmentOptionsHeader = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: p.theme.spacing(2),
  gap: p.theme.spacing()
}));

const MaxWidthContainer = styled('div')((p) => ({
  maxWidth: 900
}));

const AlertContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1),
  padding: p.theme.spacing(2),
  backgroundColor: '#fffbeb', // light yellow background
  borderRadius: p.theme.shape.borderRadius,
  marginTop: p.theme.spacing(2)
}));

export const StepParticipantsPreview = ({
  d
}: {
  d: Doc<IExperienceTemplate>;
}) => {
  const { org } = useCurrentUser();
  const theme = useTheme();
  const config = useUserRuleConfigSimple(org.customUserFields, d.data.tz, {
    orgId: d.data.orgId,
    experienceTemplateId: d.id
  });
  const { t } = useUnsafeTranslation();
  const { enrollNewUsers, enrollModifiedUsers, enrollRule } = d.data;
  if (!enrollRule) {
    return (
      <div>
        <Typography variant="h6">
          {t('No participants currently selected.')}
        </Typography>
        <div>
          {t(
            'After launching the experience, enroll participants manually from the next screen.'
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Typography
        variant="body1"
        component="p"
        style={{ marginBottom: theme.spacing(2) }}>
        {t('Enroll participants who match these filters')}:
      </Typography>
      <RuleRenderer variant="multiline" value={enrollRule} config={config} />
      {enrollNewUsers && (
        <EnrollmentOptionsRow>
          <EnrollmentOptionsHeader>
            <Check size={16} />
            <div>{t('Enroll new users, when they’re first imported')}</div>
          </EnrollmentOptionsHeader>
        </EnrollmentOptionsRow>
      )}
      {enrollModifiedUsers && (
        <EnrollmentOptionsRow>
          <EnrollmentOptionsHeader>
            <Check size={16} />
            <div>{t('Enroll existing users, when their data is changed by an import')}</div>
          </EnrollmentOptionsHeader>
        </EnrollmentOptionsRow>
      )}
    </div>
  );
};

export const StepParticipants = ({
  d,
  label,
  onChange,
  goTo,
  nextStep,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  const { org } = useCurrentUser();
  const { t } = useTranslation();
  const theme = useTheme();
  const [users, loading] = useUsersByOrgId(d.data.orgId);
  const rule = d.data.enrollRule;
  const { orgId, status } = d.data;
  console.log('enroll rule', d.data.enrollRule);

  const containerStyle = {
    marginBottom: 16
  };

  console.log('D', d);

  const participantPreviewDialog = useDialogState(false);
  const ruleBuilderConfig = useUserRuleConfigWithAutocompletion(
    orgId,
    org.customUserFields,
    d.data.tz,
    {
      orgId,
      experienceTemplateId: d.id
    }
  );

  const hasRuleBasedSelection = Boolean(d.data.enrollRule);

  // Recurring-anchor (anniversary/birthday) variant — different toggle copy,
  // different on/off semantics. Per ANN-3 / artifacts/plan_recurring_autoenroll_toggle.md.
  // anchor.field shape (set by anchors.ts in functions/experiences):
  //   calculatedFields.recurringDate.hireDate.current.date    → anniversary
  //   calculatedFields.recurringDate.dateOfBirth.current.date → birthday
  const recurringKind: 'anniversary' | 'birthday' | null = (() => {
    const a = d.data.anchor;
    if (a?.type !== ExperienceAnchorType.EMPLOYEE_DATE || !a?.isRecurring) return null;
    const field = (a as { field?: string }).field || '';
    if (field.includes('hireDate')) return 'anniversary';
    if (field.includes('dateOfBirth')) return 'birthday';
    return 'anniversary'; // sensible default for any future recurring-date fields
  })();
  const isRecurring = recurringKind !== null;

  // LDAW (Last Day at Work) = terminationDate. Default filter when toggling
  // recurring auto-enroll on so terminated employees aren't enrolled.
  // `isBlank` ignores `v` at evaluation time but the Condition type requires
  // a MatchValue (number|string|boolean), so we pass an empty string.
  const ldawIsBlankRule: Rule<string> = {
    type: 'all',
    els: [{ k: 'terminationDate', op: 'isBlank', v: '' }]
  };

  return (
    <>
      <div style={containerStyle}>
        <Grid container justify="space-between">
          <Grid item xs>
            <Typography component="h1" variant="h3">
              {label}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Section>
        <MaxWidthContainer>
          <FormRow>
            <div>
              <FormControlLabel
                label={
                  <Typography variant="h5">
                    {isRecurring
                      ? t('Auto-enroll participants every year')
                      : t('Auto-enroll participants via imports')}
                  </Typography>
                }
                control={
                  <Switch
                    color="primary"
                    checked={hasRuleBasedSelection}
                    onChange={(ev) => {
                      const enableRules = ev.target.checked;
                      if (enableRules) {
                        // Recurring anchors: drive both flags together. Backend gate
                        // (ANN-2) requires enrollNewUsers === true AND enrollRule != null.
                        // Default the rule to "LDAW is not set" so terminated employees
                        // don't get enrolled.
                        onChange((x) => ({
                          ...x,
                          data: {
                            ...x.data,
                            enrollNewUsers: isRecurring ? true : false,
                            enrollRule: isRecurring
                              ? ldawIsBlankRule
                              : { type: 'all', els: [] }
                          }
                        }));
                      } else {
                        onChange((x) => ({
                          ...x,
                          data: {
                            ...x.data,
                            enrollNewUsers: false,
                            enrollRule: null
                          }
                        }));
                      }
                    }}
                  />
                }
              />
              
              {!hasRuleBasedSelection && (
                <>
                  <Typography
                    variant="body1"
                    component="p"
                    color="textSecondary"
                    style={{ marginBottom: theme.spacing(2) }}
                  >
                    {isRecurring
                      ? t(
                          `Turn this toggle on to automatically enroll participants each year, ahead of their ${recurringKind} date.`
                        )
                      : t(
                          'Turn this toggle on to automatically add participants to this experience when they are imported into Dado for the first time; or when their data is changed by an import.'
                        )}
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    component="p"
                    color="textSecondary"
                    style={{ marginBottom: theme.spacing(2) }}
                  >
                    Whether auto-enroll is switched on or off, participants can also be enrolled in this experience via:
                  </Typography>
                  
                  <ul style={{ 
                    marginTop: 0, 
                    marginBottom: theme.spacing(2),
                    paddingLeft: theme.spacing(3)
                  }}>
                    <li>
                      <Typography variant="body2" color="textSecondary">
                        manual enrollment
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" color="textSecondary">
                        submitting an enrollment form
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" color="textSecondary">
                        an automation in another experience
                      </Typography>
                    </li>
                  </ul>
                </>
              )}

              {hasRuleBasedSelection && (
                <>
                  <Typography
                    variant="body1"
                    component="p"
                    color="textSecondary"
                    style={{ marginBottom: theme.spacing(2) }}
                  >
                    {isRecurring
                      ? t(
                          `Participants who match the filters below will be automatically enrolled each year, ahead of their ${recurringKind} date.`
                        )
                      : t(
                          'Participants who match the filters below will be automatically enrolled when an import from an external system (such as your HRIS) syncs their data to Dado.'
                        )}
                  </Typography>

                  <Typography variant="h6">
                    {t('Enrollment filters')}
                  </Typography>
                  <RuleBuilder
                    value={rule}
                    onChange={(nextRule) =>
                      onChange((x) => ({
                        ...x,
                        data: {
                          ...x.data,
                          enrollRule: nextRule
                        }
                      }))
                    }
                    config={ruleBuilderConfig}
                    nullable
                    showEmptyRowWhenNull={false}
                  />
                  <Grid container direction="row" justify="flex-end">
                    <Button
                      onClick={participantPreviewDialog.openDialog}
                      startIcon={<Eye size={16} />}
                      color="primary"
                    >
                      {t('Preview matching employees')}
                    </Button>
                    <Dialog
                      maxWidth="md"
                      fullWidth
                      open={participantPreviewDialog.dialogOpen}
                      onClose={participantPreviewDialog.closeDialog}
                    >
                      <MatchingUsers
                        users={users}
                        rule={rule}
                        loading={loading}
                        onClose={participantPreviewDialog.closeDialog}
                      />
                      <DialogActions>
                        {/* <Button
                          onClick={participantPreviewDialog.closeDialog}
                        >
                          Close
                        </Button> */}
                      </DialogActions>
                    </Dialog>
                  </Grid>

                  {d.data.anchor.type ===
                    ExperienceAnchorType.EMPLOYEE_DATE &&
                  d.data.anchor.isRecurring ? null : (
                    <>
                      <Typography variant="h6">
                        {t('Which users should be auto-enrolled')}
                      </Typography>

                      <FormRow style={{ marginBottom: 0 }}>
                        <div>
                          <FormControlLabel
                            label={t('New users, when they\'re first imported to Dado')}
                            control={
                              <Switch
                                color="primary"
                                checked={d.data.enrollNewUsers}
                                onChange={(ev) => {
                                  const enrollNewUsers = ev.target.checked;
                                  onChange((x) => ({
                                    ...x,
                                    data: {
                                      ...x.data,
                                      enrollNewUsers
                                    }
                                  }));
                                }}
                              />
                            }
                          />
                          <>
                            <Typography
                              variant="body1"
                              component="p"
                              color="textSecondary"
                              style={{ marginBottom: theme.spacing(2) }}
                            >
                              {t(
                                'Users matching the enrollment filters will be auto-enrolled when they are first imported into Dado.'
                              )}
                            </Typography>
                          </>
                        </div>
                      </FormRow>

                      <FormRow style={{ marginBottom: 0 }}>
                        <div>
                          <FormControlLabel
                            label={t('Existing users, when an import changes their data')}
                            control={
                              <Switch
                                color="primary"
                                checked={d.data.enrollModifiedUsers}
                                onChange={(ev) => {
                                  const enrollModifiedUsers =
                                    ev.target.checked;
                                  onChange((x) => ({
                                    ...x,
                                    data: {
                                      ...x.data,
                                      enrollModifiedUsers
                                    }
                                  }));
                                }}
                              />
                            }
                          />
                          <>
                            <Typography
                              variant="body1"
                              component="p"
                              color="textSecondary"
                            >
                              {t(
                                'Existing users will be auto-enrolled when an import updates their data, and the new data matches the enrollment filters.'
                              )}
                            </Typography>
                          </>
                        </div>
                      </FormRow>

                      {d.data.enrollModifiedUsers && status === 'DRAFT' && (
                        <AlertContainer>
                          <AlertCircle size={22} />
                          <Typography variant="body2">
                            {t('Existing users who already match the enrollment filters will not be auto-enrolled. You can enroll these users manually after launching the experience.')}
                          </Typography>
                        </AlertContainer>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </FormRow>
        </MaxWidthContainer>
      </Section>

      <StandardFooter d={d} onCheckChanges={onCheckChanges} onLaunchFromRoughDraft={onLaunchFromRoughDraft} isLaunchReady={isLaunchReady} onLaunch={onLaunch} lastSavedAt={lastSavedAt} />
    </>
  );
};